/**
 * TypedFloatArrayProperty<T> — A typed wrapper around ReactiveProperty<PropertyValueData>
 * for FLOAT_ARRAY property values.
 *
 * The property system stores FLOAT_ARRAY values as number[]. This wrapper
 * transparently encodes/decodes between flat number arrays and typed structures
 * (e.g., Coordinate[], RoomPlane).
 *
 * Also handles the transition period where old DB data may still contain
 * JSON objects or serialized JSON strings (from the previous JSON type).
 */

import { ReactiveProperty, Subscription, PropertyListener } from './ReactiveProperty';
import { PropertyValueData } from '../types';
import type { ReadableProperty } from './TypedJsonProperty';

type Encoder<T> = (value: T) => number[];
type Decoder<T> = (raw: unknown) => T;

/**
 * Typed wrapper around a ReactiveProperty that stores flat number arrays.
 * Provides transparent encode/decode so consumers work with typed structures.
 */
export class TypedFloatArrayProperty<T> implements ReadableProperty<T> {
  private _source: ReactiveProperty<PropertyValueData>;
  private _encode: Encoder<T>;
  private _decode: Decoder<T>;
  private _cachedRaw: unknown = undefined;
  private _cachedParsed: T | undefined = undefined;

  constructor(source: ReactiveProperty<PropertyValueData>, encode: Encoder<T>, decode: Decoder<T>) {
    this._source = source;
    this._encode = encode;
    this._decode = decode;
  }

  get value(): T {
    const raw = this._source.value;
    if (raw !== this._cachedRaw) {
      this._cachedRaw = raw;
      this._cachedParsed = this._decode(raw);
    }
    return this._cachedParsed as T;
  }

  set value(newValue: T) {
    this._cachedRaw = undefined;
    this._cachedParsed = undefined;
    this._source.value = this._encode(newValue) as unknown as PropertyValueData;
  }

  next(newValue: T): void {
    this._cachedRaw = undefined;
    this._cachedParsed = undefined;
    this._source.next(this._encode(newValue) as unknown as PropertyValueData);
  }

  subscribe(callback: PropertyListener<T>): Subscription {
    return this._source.subscribe((raw) => {
      callback(this._decode(raw));
    });
  }

  subscribeToChanges(callback: PropertyListener<T>): Subscription {
    return this._source.subscribeToChanges((raw) => {
      callback(this._decode(raw));
    });
  }

  get subscriberCount(): number {
    return this._source.subscriberCount;
  }

  destroy(): void {
    this._cachedRaw = undefined;
    this._cachedParsed = undefined;
    this._source.destroy();
  }

  get isDestroyed(): boolean {
    return this._source.isDestroyed;
  }
}
